import type { ProtocolEvent } from '@quintessence/core';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DemoSource } from './demo';
import { LiveSource } from './live';
import type { DataSource, DataState, SourceMessage } from './types';

interface DataApi extends DataState {
  setSpeed: (speed: number) => void;
}

const DataContext = createContext<DataApi | null>(null);

const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') || '';
const MAX_EVENTS = 120;

function demoParams() {
  const params = new URLSearchParams(window.location.search);
  const seed = Number(params.get('seed') ?? 7) || 7;
  const days = Math.min(30, Math.max(2, Number(params.get('warmup') ?? 6) || 6));
  return { seed, days };
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DataState>({
    kind: API_URL ? 'live' : 'demo',
    status: 'connecting',
    snapshot: null,
    events: [],
    warmup: null,
    speed: 60,
    notice: null,
  });
  const source = useRef<DataSource | null>(null);

  useEffect(() => {
    let cancelled = false;
    const emit = (msg: SourceMessage) => {
      if (cancelled) return;
      setState((s) => {
        switch (msg.type) {
          case 'snapshot':
            return { ...s, snapshot: msg.snapshot };
          case 'status':
            return { ...s, status: msg.status };
          case 'warmup':
            return { ...s, warmup: msg.warmup };
          case 'events': {
            const seen = new Set(s.events.map((e) => e.id));
            const fresh = msg.events.filter((e: ProtocolEvent) => !seen.has(e.id));
            return fresh.length ? { ...s, events: [...fresh, ...s.events].slice(0, MAX_EVENTS) } : s;
          }
        }
      });
    };

    const startDemo = (notice: string | null) => {
      const { seed, days } = demoParams();
      const demo = new DemoSource(seed, days, 60);
      source.current = demo;
      setState((s) => ({ ...s, kind: 'demo', notice }));
      demo.start(emit);
    };

    const startLive = (base: string) => {
      const live = new LiveSource(base);
      source.current = live;
      setState((s) => ({ ...s, kind: 'live', notice: null }));
      live.start(emit);
    };

    if (API_URL) {
      void LiveSource.reachable(API_URL).then((ok) => {
        if (cancelled) return;
        if (ok) startLive(API_URL);
        else startDemo('The live agent API is unreachable right now, so this page is running the simulator instead.');
      });
    } else {
      // No API configured: when the runtime serves this site itself, its API is on the same origin.
      void LiveSource.reachable('', 1500).then((ok) => {
        if (cancelled) return;
        if (ok) startLive('');
        else startDemo(null);
      });
    }

    return () => {
      cancelled = true;
      source.current?.stop();
      source.current = null;
    };
  }, []);

  const setSpeed = useCallback((speed: number) => {
    source.current?.setSpeed?.(speed);
    setState((s) => ({ ...s, speed }));
  }, []);

  const value = useMemo(() => ({ ...state, setSpeed }), [state, setSpeed]);
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData(): DataApi {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside <DataProvider>');
  return ctx;
}
